import { TypographyCaption, TypographyP2 } from '@heidi/design-system/typography';
import { cn } from '@heidi/utils';
import { AlertCircle, Check, Eye, Loader2 } from 'lucide-react';
import { defineMessages, useIntl } from 'react-intl';
import type { VisualDxDetailResult, VisualDxDiagnosis } from '../../contracts';
import { VisualDxDetailPane } from './VisualDxDetailPane';
import { VisualDxDiagnosisCards } from './VisualDxDiagnosisCards';

const messages = defineMessages({
  lookingUp: {
    id: 'kJpPyVFUNA',
    defaultMessage: 'Looking up diagnosis on VisualDx',
  },
  lookingUpWithQuery: {
    id: '/ZJn9cUXUg',
    defaultMessage: 'Looking up diagnosis on VisualDx — {query}',
  },
  enriching: {
    id: 'ezbDO2cBOz',
    defaultMessage: 'Enriching diagnoses with VisualDx',
  },
  enrichingWithQuery: {
    id: 'm84aGBzkKT',
    defaultMessage: 'Enriching diagnoses with VisualDx — {query}',
  },
  loaded: {
    id: '9OxezL/nWF',
    defaultMessage: 'VisualDx diagnosis loaded',
  },
  linksAdded: {
    id: 'qBlg+soYZi',
    defaultMessage: 'VisualDx diagnosis links added',
  },
  lookupFailed: {
    id: 'J5jtUyuw7+',
    defaultMessage: 'VisualDx lookup failed',
  },
  completed: {
    id: 'fT7uaunC4U',
    defaultMessage: 'completed',
  },
});

interface VisualDxBlockProps {
  tool: 'get_diagnostic_info' | 'enrich_diagnoses_with_links';
  status: 'fetching' | 'completed' | 'error';
  query?: string;
  error?: string;
  className?: string;
  /** Structured diagnoses from a completed ``enrich_diagnoses_with_links`` call. */
  diagnoses?: VisualDxDiagnosis[];
  /** Compare-all URL from a completed ``enrich_diagnoses_with_links`` call. */
  compare_url?: string | null;
  /** Structured detail from a completed ``get_diagnostic_info`` call. */
  detail?: VisualDxDetailResult;
  /**
   * Called when the user clicks a diagnosis card.
   * Consumers can wire this to a follow-up chat prompt.
   */
  onDiagnosisSelect?: (diagnosisName: string) => void;
}

/**
 * Renders a static (non-interactive) VisualDx tool result inside the chat.
 *
 * Specifically scoped to ``get_diagnostic_info`` (collapsible detail pane)
 * and ``enrich_diagnoses_with_links`` (clickable diagnosis card grid).
 *
 * The interactive diagnostic-flow surface lives in
 * {@link ./InteractiveWidgetBlock.tsx} — it goes through the generic
 * ``interactive_widget`` chunk type so non-derm widgets can share the
 * same iframe + postMessage plumbing.
 */
export const VisualDxBlock = ({
  tool,
  status,
  query,
  error,
  className,
  diagnoses,
  compare_url,
  detail,
  onDiagnosisSelect,
}: VisualDxBlockProps): JSX.Element => {
  const intl = useIntl();

  const isGetDiagnostic = tool === 'get_diagnostic_info';
  const fetchingLabel = query
    ? intl.formatMessage(
        isGetDiagnostic ? messages.lookingUpWithQuery : messages.enrichingWithQuery,
        { query }
      )
    : intl.formatMessage(isGetDiagnostic ? messages.lookingUp : messages.enriching);

  if (status === 'fetching') {
    return (
      <div
        className={cn(
          'relative w-full min-w-0 overflow-hidden rounded-lg border border-border duration-300 animate-in fade-in',
          className
        )}
      >
        <div className="flex items-center p-3">
          <div className="flex min-w-0 flex-1 items-center gap-2">
            <Loader2 className="size-4 shrink-0 animate-spin text-text-secondary" />
            <TypographyP2 className="min-w-0 truncate text-text-secondary">
              {fetchingLabel}
            </TypographyP2>
          </div>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div
        className={cn(
          'w-full min-w-0 overflow-hidden rounded-lg border border-validation-destructive-border bg-validation-destructive-muted duration-300 animate-in fade-in',
          className
        )}
      >
        <div className="flex items-center gap-2 p-3">
          <AlertCircle className="size-4 shrink-0 text-validation-destructive" />
          <TypographyP2 className="w-0 flex-1 truncate text-validation-destructive">
            {error || intl.formatMessage(messages.lookupFailed)}
          </TypographyP2>
        </div>
      </div>
    );
  }

  const hasEnrichedDiagnoses = !isGetDiagnostic && diagnoses && diagnoses.length > 0;
  const hasDetail = isGetDiagnostic && detail;

  const headerLabel = intl.formatMessage(
    isGetDiagnostic ? messages.loaded : messages.linksAdded
  );

  return (
    <div
      className={cn(
        'w-full min-w-0 overflow-hidden rounded-lg border border-border duration-300 animate-in fade-in',
        className
      )}
    >
      <div className="flex items-center gap-2 p-3">
        <Eye className="size-4 shrink-0 text-text-secondary" />
        <TypographyP2 className="w-0 flex-1 truncate text-text-secondary">
          {headerLabel}
        </TypographyP2>
        <TypographyCaption className="flex shrink-0 items-center gap-1 whitespace-nowrap text-text-tertiary duration-200 animate-in fade-in">
          <Check className="size-3.5" />
          {intl.formatMessage(messages.completed)}
        </TypographyCaption>
      </div>

      {hasEnrichedDiagnoses && (
        <div className="border-t border-border p-3 pt-2">
          <VisualDxDiagnosisCards
            diagnoses={diagnoses}
            compare_url={compare_url}
            onSelect={onDiagnosisSelect}
          />
        </div>
      )}

      {hasDetail && (
        <div className="border-t border-border p-3 pt-2">
          <VisualDxDetailPane detail={detail} />
        </div>
      )}
    </div>
  );
};
